import React, { useState, useEffect } from 'react';
import tenantApi, { dashboardApi } from '../services/tenantApi';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { RefreshCw, Download } from 'lucide-react';
import { Button } from './ui/button';

const RoomTypePriceTable = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userContext, setUserContext] = useState(null);
  const [roomMappings, setRoomMappings] = useState([]);
  const [priceData, setPriceData] = useState([]);
  const [competitorHotels, setCompetitorHotels] = useState([]);
  const [selectedRoomType, setSelectedRoomType] = useState('');
  const [tableData, setTableData] = useState({ dates: [], hotels: [], averages: {} });

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get user context
        const context = await dashboardApi.getUserContext();
        setUserContext(context);

        const hotelId = context.hotel_id;
        console.log('🏨 Using hotel ID for room mappings:', hotelId);
        
        if (!hotelId) {
          throw new Error('Hotel ID not found');
        }

        // Fetch all required data (use same API calls as CompetitorRateMatrix)
        console.log('🔍 Fetching data for hotel ID:', hotelId);
        
        const [mappingsResponse, priceResponse, competitorResponse] = await Promise.all([
          tenantApi.get(`/api/tenants/hotels/${hotelId}/room-mappings`),
          dashboardApi.getPriceFeedData(14), // Same as CompetitorRateMatrix
          dashboardApi.getCompetitorHotels(20)
        ]);

        console.log('📊 Raw API responses:', { 
          mappings: mappingsResponse.data, 
          prices: priceResponse.items?.length,
          competitors: competitorResponse.hotels?.length 
        });

        const mappings = mappingsResponse.data?.room_mappings || [];
        const prices = priceResponse.items || [];
        const competitors = competitorResponse.hotels || [];
        const userHotelId = priceResponse.user_hotel_id; // Same as CompetitorRateMatrix

        setRoomMappings(mappings);
        setPriceData(prices);
        setCompetitorHotels(competitors);

        // Set default room type (first available)
        const roomTypes = [...new Set(mappings.map(m => m.hotel_room_types?.name).filter(Boolean))];
        if (roomTypes.length > 0) {
          setSelectedRoomType(roomTypes[0]);
        }

        console.log('📊 Loaded data:', { mappings: mappings.length, prices: prices.length, competitors: competitors.length });

      } catch (err) {
        console.error('Error loading initial data:', err);
        setError('Failed to load room type data');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Helper function to get hotel name from OTA hotel ID
  const getHotelNameFromOtaId = (otaHotelId) => {
    if (!otaHotelId) return `Hotel ID: ${otaHotelId}`;
    
    console.log(`🔍 Trying to convert OTA ID: ${otaHotelId}`);
    console.log(`📊 Available price data hotels:`, [...new Set(priceData.map(item => `${item.hotel_name} (ID: ${item.hotel_id})`))].slice(0, 10));
    
    // Method 1: Find hotel name from price data using hotel_id field (which contains OTA IDs)
    if (priceData.length > 0) {
      const priceItem = priceData.find(item => item.hotel_id === otaHotelId.toString());
      if (priceItem && priceItem.hotel_name) {
        console.log(`✅ Converting OTA ID ${otaHotelId} → ${priceItem.hotel_name} (from price data)`);
        return priceItem.hotel_name;
      }
      
      // Try alternative matching - sometimes the OTA ID might be stored differently
      const altPriceItem = priceData.find(item => 
        item.hotel_name && item.hotel_name.toLowerCase().includes('sindhu') && item.hotel_name.toLowerCase().includes('mertha')
      );
      if (altPriceItem && otaHotelId.toString().toLowerCase().includes('sindhu')) {
        console.log(`✅ Alternative match: OTA ID ${otaHotelId} → ${altPriceItem.hotel_name} (fuzzy match)`);
        return altPriceItem.hotel_name;
      }
    }
    
    // Method 2: Try to find in competitor hotels by matching IDs
    const competitorHotel = competitorHotels.find(h => 
      h.id === otaHotelId.toString() || 
      h.ota_hotel_id === otaHotelId.toString()
    );
    if (competitorHotel) {
      console.log(`✅ Converting OTA ID ${otaHotelId} → ${competitorHotel.name} (from competitor API)`);
      return competitorHotel.name;
    }
    
    // Method 3: If the otaHotelId already looks like a hotel name, use it directly
    if (otaHotelId.toString().includes(' ') && otaHotelId.toString().length > 10) {
      console.log(`✅ Using OTA ID as hotel name: ${otaHotelId}`);
      return otaHotelId.toString();
    }
    
    // If not found anywhere, return a descriptive label
    console.log(`❌ No hotel name found for OTA ID: ${otaHotelId}`);
    return `Hotel ID: ${otaHotelId}`;
  };

  // Process table data when room type selection changes
  useEffect(() => {
    if (!selectedRoomType || !roomMappings.length || !priceData.length) {
      console.log('⏳ Waiting for data:', { 
        selectedRoomType: !!selectedRoomType, 
        roomMappings: roomMappings.length, 
        priceData: priceData.length 
      });
      return;
    }

    const processTableData = async () => {
      console.log('🔄 Processing table data for room type:', selectedRoomType);

      // Get mappings for selected room type
      const roomTypeMappings = roomMappings.filter(
        m => m.hotel_room_types?.name === selectedRoomType
      );

      if (roomTypeMappings.length === 0) {
        setTableData({ dates: [], hotels: [], averages: {} });
        return;
      }

      // PRE-FETCH room type analysis data for "Your Hotel"
      let roomTypeAnalysisData = null;
      try {
        console.log('🔍 PRE-FETCHING room type analysis data for:', selectedRoomType);
        roomTypeAnalysisData = await dashboardApi.getRoomTypeAnalysis(selectedRoomType, 14);
        console.log('✅ Room type analysis data fetched:', {
          hasData: !!roomTypeAnalysisData,
          matrixDataLength: roomTypeAnalysisData?.matrix_data?.length || 0,
          userHotel: roomTypeAnalysisData?.user_hotel
        });
      } catch (error) {
        console.warn('❌ Failed to fetch room type analysis data:', error);
      }

      // Resolve the tenant hotel's exact price feed name from room type analysis data
      const userHotelName = roomTypeAnalysisData?.user_hotel || '';
      console.log('🏨 Tenant hotel name from analysis:', userHotelName);

      // Get ALL available hotels from price data (not just mapped ones)
      const allHotelsInPriceData = [...new Set(priceData.map(item => item.hotel_name))];
      const hotels = ['Your Hotel']; // Start with user's hotel
      
      // Add all competitor hotels from price data (exclude tenant's own hotel)
      allHotelsInPriceData.forEach(hotelName => {
        if (hotelName && hotelName !== userHotelName && !hotels.includes(hotelName)) {
          hotels.push(hotelName);
        }
      });
      
      // Also include hotels from room mappings (in case some aren't in current price data)
      const competitorOtaIds = [...new Set(roomTypeMappings.map(m => m.competitor_hotel_name))];
      competitorOtaIds.forEach(otaId => {
        const hotelName = getHotelNameFromOtaId(otaId);
        if (hotelName && !hotels.includes(hotelName)) {
          hotels.push(hotelName);
        }
      });

      console.log('🏨 Hotels in analysis:', hotels);
      console.log('🔗 OTA ID mappings:', competitorOtaIds.map(id => ({
        otaId: id,
        hotelName: getHotelNameFromOtaId(id)
      })));

      // Debug: Show available price data dates
      const availableDates = [...new Set(priceData.map(item => item.checkin_date))].sort();
      console.log('📅 Available price data dates:', availableDates.slice(0, 10));
      console.log('🏨 Available hotels in price data:', [...new Set(priceData.map(item => item.hotel_name))]);
      console.log('🛏️ Room type mappings for', selectedRoomType + ':', roomTypeMappings.map(m => ({
        hotel: m.competitor_hotel_name,
        roomType: m.competitor_room_type
      })));

      // Generate next 14 days (consistent with 14-Day Competitor Rate Matrix)
      const dates = [];
      for (let i = 0; i < 14; i++) {
        const date = new Date();
        date.setDate(date.getDate() + i);
        const dateStr = date.toISOString().split('T')[0];
        const formattedDate = date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });

        const dateData = {
          date: dateStr,
          formatted: formattedDate,
          prices: {}
        };

        // Get prices for each hotel on this date
        for (const hotelName of hotels) {
          let price = null;

          console.log(`🔍 Looking for ${hotelName} price on ${dateStr}`);

          if (hotelName === 'Your Hotel') {
            // Use pre-fetched room type analysis data
            if (roomTypeAnalysisData && roomTypeAnalysisData.matrix_data) {
              const dateData = roomTypeAnalysisData.matrix_data.find(item => item.date === dateStr);
              price = dateData?.prices?.[userHotelName] || null;
              
              console.log(`  📊 NEW ENDPOINT RESULT for ${dateStr}:`, {
                hasAnalysisData: !!roomTypeAnalysisData,
                dateData: !!dateData,
                allPricesForDate: dateData?.prices || {},
                sanurLodgePrice: price,
                userHotel: roomTypeAnalysisData.user_hotel
              });
            } else {
              // Fallback to old method if no analysis data
              const yourPrice = priceData.find(item => 
                item.hotel_name === userHotelName && 
                item.checkin_date === dateStr &&
                roomTypeMappings.some(m => 
                  item.room_type_name === m.competitor_room_type ||
                  item.room_type_name.toLowerCase().includes(selectedRoomType.toLowerCase().split(' ')[0])
                )
              );
              price = yourPrice ? yourPrice.price : null;
              console.log(`  📊 FALLBACK RESULT for ${dateStr}:`, price, yourPrice ? `(${yourPrice.room_type_name})` : '(not found)');
            }
          } else {
            // Find competitor price - try mapped room type first, then fallback to similar room types
            const mapping = roomTypeMappings.find(m => {
              const mappedHotelName = getHotelNameFromOtaId(m.competitor_hotel_name);
              return mappedHotelName === hotelName;
            });

            console.log(`  ${hotelName} mapping:`, mapping ? `${mapping.competitor_hotel_name} -> ${mapping.competitor_room_type}` : 'not found');

            if (mapping) {
              // Use multiple matching strategies for better price finding
              let competitorPrice = priceData.find(item =>
                (item.hotel_id === mapping.competitor_hotel_name || item.hotel_name === hotelName) &&
                item.checkin_date === dateStr &&
                item.room_type_name === mapping.competitor_room_type
              );
              
              // If exact match fails, try fuzzy matching for Sindhu Mertha
              if (!competitorPrice && hotelName.toLowerCase().includes('sindhu')) {
                competitorPrice = priceData.find(item =>
                  item.hotel_name && 
                  item.hotel_name.toLowerCase().includes('sindhu') && 
                  item.hotel_name.toLowerCase().includes('mertha') &&
                  item.checkin_date === dateStr &&
                  item.room_type_name === mapping.competitor_room_type
                );
                if (competitorPrice) {
                  console.log(`🎯 Fuzzy match found for ${hotelName}: ${competitorPrice.hotel_name}`);
                }
              }
              
              // If still no match, try by OTA ID only
              if (!competitorPrice) {
                competitorPrice = priceData.find(item =>
                  item.hotel_id === mapping.competitor_hotel_name &&
                  item.checkin_date === dateStr
                );
                if (competitorPrice) {
                  console.log(`🎯 OTA ID match found for ${hotelName}: ${competitorPrice.hotel_name}`);
                }
              }
              
              price = competitorPrice ? competitorPrice.price : null;
              console.log(`  ${hotelName} price:`, price, competitorPrice ? `(found: ${competitorPrice.room_type_name})` : '(not found in price data)');
            } else {
              // No specific mapping found - try to find similar room type by matching keywords
              const selectedRoomKeywords = selectedRoomType.toLowerCase().split(' ');
              const competitorPrice = priceData.find(item =>
                item.hotel_name === hotelName &&
                item.checkin_date === dateStr &&
                selectedRoomKeywords.some(keyword => 
                  item.room_type_name.toLowerCase().includes(keyword) && keyword.length > 2
                )
              );
              
              // If no similar room type found, get the cheapest room for this hotel on this date
              if (!competitorPrice) {
                const hotelRoomsForDate = priceData.filter(item =>
                  item.hotel_name === hotelName && item.checkin_date === dateStr && item.price > 0
                );
                if (hotelRoomsForDate.length > 0) {
                  const cheapestRoom = hotelRoomsForDate.reduce((min, current) => 
                    current.price < min.price ? current : min
                  );
                  price = cheapestRoom.price;
                  console.log(`  ${hotelName} price (cheapest room):`, price, `(${cheapestRoom.room_type_name})`);
                }
              } else {
                price = competitorPrice.price;
                console.log(`  ${hotelName} price (similar room):`, price, `(${competitorPrice.room_type_name})`);
              }
            }
          }

          if (price) {
            dateData.prices[hotelName] = price;
          }
        }

        console.log(`📊 ${dateStr} final prices:`, dateData.prices);

        dates.push(dateData);
      }

      setTableData({ dates, hotels, averages: {} });
      console.log('✅ Table data processed:', { dates: dates.length, hotels: hotels.length });
    };

    processTableData();
  }, [selectedRoomType, roomMappings, priceData, competitorHotels]);

  // Format price for display
  const formatPrice = (price) => {
    if (!price) return '-';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: 'compact'
    }).format(price);
  };

  // Simple color coding: Green = Lowest, Amber = Middle, Red = Highest
  const getPriceCellStyle = (price, hotelName, allPricesInRow) => {
    if (!price || price <= 0) {
      return 'text-gray-400 bg-gray-50';
    }
    
    // Get all valid prices for this row (excluding null/undefined)
    const validPrices = Object.values(allPricesInRow).filter(p => p && p > 0);
    
    if (validPrices.length === 0) {
      return 'text-gray-700 bg-white';
    }
    
    const minPrice = Math.min(...validPrices);
    const maxPrice = Math.max(...validPrices);
    
    // Debug logging
    console.log(`🎨 Simple color for ${hotelName}:`, { 
      price, 
      minPrice, 
      maxPrice, 
      isMin: price === minPrice, 
      isMax: price === maxPrice 
    });
    
    // Apply simple color logic
    if (price === minPrice) {
      // Lowest price = Green
      return 'text-green-700 font-bold bg-green-100 border-l-4 border-green-500';
    } else if (price === maxPrice) {
      // Highest price = Red
      return 'text-red-700 font-bold bg-red-100 border-l-4 border-red-500';
    } else {
      // Middle prices = Amber
      return 'text-amber-700 font-bold bg-amber-100 border-l-4 border-amber-500';
    }
  };

  // Get available room types
  const availableRoomTypes = [...new Set(roomMappings.map(m => m.hotel_room_types?.name).filter(Boolean))];

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Room Type Supplement Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Loading room type pricing data...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Room Type Supplement Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (availableRoomTypes.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Room Type Supplement Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              No room type mappings found. Please create room type mappings in Settings → Room Mappings to see competitor price comparisons.
            </p>
            <Button asChild variant="outline">
              <a href="/settings">Go to Room Mappings</a>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Room Type Supplement Analysis</span>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Room Type Selector */}
        <div className="mb-6">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium">Room Type:</label>
            <Select value={selectedRoomType} onValueChange={setSelectedRoomType}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Select room type" />
              </SelectTrigger>
              <SelectContent>
                {availableRoomTypes.map(roomType => (
                  <SelectItem key={roomType} value={roomType}>
                    {roomType}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Badge variant="secondary">
              {tableData.dates.filter(d => Object.keys(d.prices).length > 0).length} days with data
            </Badge>
          </div>
        </div>

        {/* Price Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">Date</TableHead>
                {tableData.hotels.map(hotel => (
                  <TableHead key={hotel} className="text-center font-semibold">
                    {hotel}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {tableData.dates.map(dateData => (
                <TableRow key={dateData.date}>
                  <TableCell className="font-medium">{dateData.formatted}</TableCell>
                  {tableData.hotels.map(hotel => {
                    const price = dateData.prices[hotel];
                    const cellStyle = getPriceCellStyle(price, hotel, dateData.prices);
                    return (
                      <TableCell 
                        key={hotel} 
                        className={`text-center transition-colors duration-200 ${cellStyle}`}
                      >
                        <div className="px-2 py-1 rounded">
                          {formatPrice(price)}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Simple Color Legend */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Color Legend</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center space-x-3">
              <div className="w-4 h-6 bg-green-100 border-l-4 border-green-500 rounded-sm"></div>
              <div>
                <span className="font-medium text-green-700">Lowest Price</span>
                <p className="text-xs text-gray-600">Best deal for this date</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-4 h-6 bg-amber-100 border-l-4 border-amber-500 rounded-sm"></div>
              <div>
                <span className="font-medium text-amber-700">Competitive</span>
                <p className="text-xs text-gray-600">Mid-range pricing</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-4 h-6 bg-red-100 border-l-4 border-red-500 rounded-sm"></div>
              <div>
                <span className="font-medium text-red-700">Highest Price</span>
                <p className="text-xs text-gray-600">Most expensive for this date</p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RoomTypePriceTable;
